import type { ReactNode } from "react";
import { cx } from "@/shared/lib/cx";

export interface PageHeaderProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  count?: ReactNode;
  status?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  count,
  status,
  actions,
  className,
}: PageHeaderProps) {
  return (
    <header className={cx("dw-page-header card border border-base-300 bg-base-100 shadow-card", className)}>
      <div className="card-body flex-row flex-wrap items-center gap-4 p-4 lg:p-5">
        <div className="min-w-0 flex-1 basis-72">
          {eyebrow && (
            <p className="mb-1 text-[11px] font-bold uppercase tracking-[.07em] text-muted">{eyebrow}</p>
          )}
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h1 className="m-0 truncate text-2xl font-bold leading-tight text-base-content">{title}</h1>
            {count !== undefined && (
              <span className="badge badge-neutral badge-sm font-semibold">{count}</span>
            )}
            {status}
          </div>
          {description && (
            <p className="mb-0 mt-1 max-w-3xl text-[13px] leading-5 text-muted">{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">{actions}</div>
        )}
      </div>
    </header>
  );
}

export interface SectionHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function SectionHeader({ title, description, actions, className }: SectionHeaderProps) {
  return (
    <div className={cx("flex min-w-0 flex-wrap items-start gap-3", className)}>
      <div className="min-w-0 flex-1">
        <h2 className="dw-section-title">{title}</h2>
        {description && <p className="dw-section-copy">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}
    </div>
  );
}
